<template>
  <v-card>
    <v-row class="pa-1">
      <v-col class="shrink pr-2">
        <vs-button icon="arrow_back" type="tertiary" @click="closeChatWindow" />
      </v-col>
      <v-col align-self="center" :style="{ minWidth: 0 }">
        <vs-text type="intro" truncate>{{ title }}</vs-text>
      </v-col>
      <v-col class="shrink pl-2">
        <vs-menu :items="participants" horizontal-position="right" :max-height="400">
          <template #activator>
            <vs-button icon="account_circle" type="tertiary" />
          </template>
        </vs-menu>
      </v-col>
    </v-row>

    <v-divider />

    <v-card-text id="chat-container" ref="chatWindow" @scroll.passive="onScroll">
      <vs-wrapper space="small">
        <div v-if="store.loading" class="text-center">
          <v-progress-circular :size="64" :width="6" indeterminate color="primary" />
        </div>
        <template v-for="(message, index) in messages" v-else>
          <div v-if="typeof message === 'string'" :key="message" class="section-header">
            <vs-text type="overline">{{ message }}</vs-text>
          </div>

          <ChatMessage
            v-else
            :id="`message-${message.id}`"
            :key="`message-${message.id}`"
            :message="message"
            :seen-by="seenBy(message, index + 1)"
            :is-message-from-myself="isMessageFromMyself(message)"
            :is-message-automated="isMessageAutomated(message)"
          />
        </template>
      </vs-wrapper>
    </v-card-text>

    <v-divider />

    <div class="pa-2" v-if="store.channels[channelId]">
      <vs-text-input
        v-model="chatMessageText"
        label="Type your message"
        hide-mini-label
        multiline
        autofocus
        rows="1"
        @keydown.enter.prevent="sendMessage"
        @focus="store.channelRead(channelId)"
      >
        <template #append>
          <v-icon color="#065fd4" @click="sendMessage">send</v-icon>
        </template>
      </vs-text-input>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import ChatMessage from './ChatMessage.vue'
import { Timestamp } from 'firebase/firestore'
import moment from 'moment'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useChatStore } from './useChatData'
import { useGetters } from 'vuex-composition-helpers'
import { Message } from './models'
import { useCurrentUser } from '@/common/useCurrentUser'
import { storeToRefs } from 'pinia'
import { formatDateWithYear } from '@/utils/date-formatting'

const props = withDefaults(
  defineProps<{
    title: string
    channelId: string
  }>(),
  { title: 'Loading...' },
)

const emits = defineEmits<{
  (event: 'close-chat')
}>()

const chatWindow = ref()
const chatMessageText = ref<string>('')
const lastMessageIdBeforeLoading = ref<any>()

const store = useChatStore()
const { messages: messagesRaw } = storeToRefs(store)

const messages = computed(() => {
  let previousDate
  const groupedMessages = messagesRaw.value[props.channelId]?.reduce((groups, message) => {
    const date = formatDateWithYear(message.timestamp.toDate())
    if (previousDate !== date) {
      groups.push(date)
      previousDate = date
    }

    groups.push(message)
    return groups
  }, [] as Message[])

  return groupedMessages
})

const { getBySid: getUserBySid } = useGetters('users', ['getBySid'])

const currentUser = useCurrentUser()

const participants = computed(() => {
  if (!store.channels[props.channelId]) return

  const userInfo = store.channels[props.channelId]?.participants.reduce((results, sid) => {
    const user = getUserBySid.value(sid)
    if (user) {
      results.push({ text: user.name, description: user.email })
    }
    return results
  }, [] as any)

  return [{ header: 'participants' }, ...userInfo]
})

watch(messages, (incomingMessages, existingMessages) => {
  handleMessagesUpdated(incomingMessages ?? [], existingMessages ?? [])

  if (chatWindow.value.scrollTop === 0 && lastMessageIdBeforeLoading.value) {
    keepScrollPosition()
  }
})

onMounted(() => {
  scrollToBottom()
})

const keepScrollPosition = () => {
  const lastMessageOffset = document.getElementById(
    `message-${lastMessageIdBeforeLoading.value}`,
  )?.offsetTop

  chatWindow.value.scrollTop = lastMessageOffset
  lastMessageIdBeforeLoading.value = null
}

const handleMessagesUpdated = (incomingMessages: Message[], existingMessages: Message[]) => {
  const incomingNewestMessage = incomingMessages.slice(-1)[0]
  const existingNewestMessage = existingMessages.slice(-1)[0]

  const receivedNewMessage = incomingNewestMessage?.id !== existingNewestMessage?.id

  if (receivedNewMessage) {
    nextTick(() => scrollToBottom())

    if (document.hasFocus()) {
      store.channelRead(props.channelId)
    }
  }
}

const closeChatWindow = () => {
  emits('close-chat')
}

const isMessageFromMyself = (message: Message) => {
  return message.from === currentUser.value?.userSid && !isMessageAutomated(message)
}

const isMessageAutomated = (message: Message) => {
  return message.source === 'MESSAGE_SOURCE.automated'
}

const sendMessage = () => {
  if (chatMessageText.value === '') {
    return
  }

  const messageBody = {
    from: currentUser.value?.userSid,
    fromName: currentUser.value?.name,
    timestamp: Timestamp.now(),
    version: 1,
    message: chatMessageText.value,
  }

  store.sendChatMessage(props.channelId, messageBody)

  chatMessageText.value = ''
}

const scrollToBottom = () => {
  if (!chatWindow.value) return

  nextTick(() => (chatWindow.value.scrollTop = chatWindow.value.scrollHeight))
}

const onScroll = () => {
  if (chatWindow.value.scrollTop === 0 && !lastMessageIdBeforeLoading.value) {
    lastMessageIdBeforeLoading.value = messages.value[1].id
    store.fetchPreviousMessages(props.channelId)
  }
}

const seenBy = (message: Message, nextMessageIndex: number) => {
  const receipts = store.channels[props.channelId]?.read
  const messageSentTime = message.timestamp.toDate()

  let nextMessage = messages.value[nextMessageIndex]
  if (typeof nextMessage === 'string') {
    nextMessage = messages.value[nextMessageIndex + 1]
  }

  const nextMessageSentTime = nextMessage && nextMessage.timestamp.toDate()

  return Object.keys(receipts).reduce((readReceipts, sid) => {
    const readTime = moment(receipts[sid].toDate())

    if (
      readTime.isAfter(messageSentTime) &&
      (!nextMessageSentTime || readTime.isBefore(nextMessageSentTime)) &&
      message.from !== sid
    ) {
      const user = getUserBySid.value(sid)
      if (user) {
        readReceipts.push({
          name: user.name,
          timestamp: readTime.format('MMM DD, YYYY HH:mm'),
        })
      }
    }
    return readReceipts
  }, [] as any)
}
</script>

<style scoped>
#chat-container {
  height: 430px;
  background-color: var(--color-silver);
  overflow-y: auto;
  overflow-x: hidden;
}

.section-header {
  text-align: center;
  padding: var(--space-base);
}
</style>
